import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { useToast } from 'src/@/components/ui/use-toast';
import propostasService from 'src/services/propostasService';
import EditorTopBar from './editor/EditorTopBar';
import PropostaSidebar from './editor/PropostaSidebar';
import DocumentCanvas from './editor/DocumentCanvas';
import VisualizadorDocumento from './editor/VisualizadorDocumento';
import CanvasEditor from './editor/canvas/CanvasEditor';
import CanvasReadOnly from './editor/canvas/CanvasReadOnly';
import { blocosParaCanvas, adicionarTextoAoCanvas, documentoIaParaCanvas, corDeFundoCanvas, aplicarCorDeFundo } from './editor/canvas/canvasUtils';
import CompartilharPropostaDialog from './CompartilharPropostaDialog';
import { PROPOSTAS_DESIGN } from './Propostas.design';
import { getModeloPreProntoById } from './modelosPreProntosData';
import { caminhoEdicao } from './propostasRotas';
import { DOCUMENTO_DESIGN as D } from './EditorDocumento.design';

const DEFAULT_PROPOSTA = {
  titulo: 'Nova Proposta Comercial',
  lead_id: null,
  chat_id: null,
  tarefa_id: null,
  contato: { nome: '', email: '', telefone: '' },
  layout: {
    tema_cor: '#7C3AED',
    logo_url: '',
    empresa: { nome: '', cnpj: '', telefone: '', email: '', endereco: '' },
    rodape_texto: 'Ficamos à disposição para qualquer dúvida em relação à proposta.'
  },
  blocos: [
    { tipo: 'data', titulo: '', conteudo_html: '', config: { cidade: '' }, ordem: 0 },
    { tipo: 'dados_cliente', titulo: '', conteudo_html: '', config: {}, ordem: 1 },
    { tipo: 'texto', titulo: 'Apresentação', conteudo_html: '', config: {}, ordem: 2 },
    { tipo: 'itens', titulo: '', conteudo_html: '', config: {}, ordem: 3 },
    { tipo: 'forma_pagamento', titulo: 'Forma de pagamento', conteudo_html: '', config: {}, ordem: 4 },
    { tipo: 'validade', titulo: '', conteudo_html: '', config: {}, ordem: 5 },
    { tipo: 'observacoes', titulo: 'Observações', conteudo_html: '', config: {}, ordem: 6 }
  ],
  itens: [{ descricao: '', quantidade: 1, preco_unitario: 0, desconto_percent: 0 }],
  desconto_global_percent: 0,
  validade_dias: 15
};

export default function PropostaEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const isEditing = Boolean(id);
  const [proposta, setProposta] = useState(DEFAULT_PROPOSTA);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedTarefa, setSelectedTarefa] = useState(null);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const assinaturaDebounceRef = useRef(null);

  // Vindo do dropdown "Nova Proposta" da listagem: força o modo (independente do
  // que o modelo padrão define) e opcionalmente já deixa o composer do Agente de
  // IA focado (atalho "Criar com IA").
  const modoParam = searchParams.get('modo');
  const autoFocusIa = searchParams.get('ia') === '1';
  // Vindo da tela de Modelos: aplica um modelo salvo (template) ou um modelo
  // pré-pronto da biblioteca (catalogo, não persistido) direto na criação.
  const templateIdParam = searchParams.get('template');
  const catalogoParam = searchParams.get('catalogo');

  // O Voltar precisa devolver o usuário ao contexto de origem: quem abriu a
  // proposta pelo card de um negócio caía na listagem geral e perdia o lead que
  // estava vendo. `voltar` é um nome de origem conhecido, não uma URL livre,
  // para a tela não virar um redirecionador aberto.
  const voltarParam = searchParams.get('voltar');
  const leadIdRetorno = searchParams.get('lead_id');
  const backTo = voltarParam === 'negocio' && leadIdRetorno
    ? `/dashboard/negocios?lead_id=${leadIdRetorno}`
    : '/dashboard/propostas';

  // Modo conversa: o chat do agente toma a sidebar inteira (alargada) e a paleta
  // de elementos do canvas some, dando espaço à conversa. Abre direto no atalho
  // "Criar com IA"; nos demais casos, pelo botão "Conversar com o agente".
  const [chatAberto, setChatAberto] = useState(() => autoFocusIa && !isEditing);

  useEffect(() => {
    if (isEditing) {
      loadProposta();
    } else {
      const leadIdParam = searchParams.get('lead_id');
      if (leadIdParam) {
        setProposta(prev => ({ ...prev, lead_id: leadIdParam }));
      }

      // Deep link vindo de uma conversa: sem isso o parâmetro é descartado em
      // silêncio e a proposta nasce sem vínculo com o chat (não volta a aparecer
      // na sidebar e o envio por WhatsApp fica indisponível).
      const chatIdParam = searchParams.get('chat_id');
      if (chatIdParam) {
        setProposta(prev => ({ ...prev, chat_id: chatIdParam }));
      }

      if (autoFocusIa) {
        // "Criar com IA": documento em branco, sem modelo padrão pré-selecionado —
        // quem preenche o conteúdo é o Agente de IA (ou o usuário à mão), não o
        // modelo. A lista de modelos continua carregada só para o seletor do topo,
        // caso o usuário prefira escolher um manualmente depois.
        setProposta(prev => ({
          ...prev,
          modo_design: 'canvas',
          canvas: { paginas: [{ fundo_cor: '#ffffff', elementos: [] }] },
          blocos: [],
          itens: []
        }));
        loadTemplates();
      } else if (catalogoParam) {
        // Modelo da biblioteca pré-pronta (não persistido): aplica direto, sem
        // auto-selecionar o modelo padrão do cliente.
        loadTemplates();
        applyCatalogTemplate(getModeloPreProntoById(catalogoParam));
      } else if (templateIdParam) {
        loadTemplates().then((list) => {
          const found = list.find(t => t._id === templateIdParam);
          if (found) applyTemplate(found);
          aplicarModoForcado();
        });
      } else {
        loadTemplates().then((list) => {
          if (list.length > 0) {
            const padrao = list.find(t => t.padrao) || list[0];
            applyTemplate(padrao);
          }
          aplicarModoForcado();
        });
      }
    }
  }, [id]);

  // O dropdown "Nova Proposta" (Orçamento/Proposta) passa ?modo= para forçar o
  // modo de edição independente do que o modelo aplicado definir.
  const aplicarModoForcado = () => {
    if (modoParam !== 'blocos' && modoParam !== 'canvas') return;
    setProposta(prev => ({
      ...prev,
      modo_design: modoParam,
      canvas: modoParam === 'canvas' && !prev.canvas?.paginas?.length ? blocosParaCanvas(prev) : prev.canvas
    }));
  };

  const loadTemplates = async () => {
    try {
      const response = await propostasService.listarTemplates(user?.clienteId);
      if (response.error) return [];
      const list = response.data || [];
      setTemplates(list);
      return list;
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
      return [];
    }
  };

  const applyTemplate = (template) => {
    if (!template) return;
    setSelectedTemplateId(template._id);
    setProposta(prev => ({
      ...prev,
      template_id: template._id,
      titulo: prev.titulo === DEFAULT_PROPOSTA.titulo || !prev.titulo ? template.nome : prev.titulo,
      layout: template.layout || prev.layout,
      blocos: template.blocos && template.blocos.length > 0 ? template.blocos : prev.blocos,
      modo_design: template.modo_design || 'blocos',
      canvas: template.canvas?.paginas?.length ? template.canvas : prev.canvas,
      itens: template.itens_padrao && template.itens_padrao.length > 0 ? template.itens_padrao : prev.itens,
      validade_dias: template.validade_dias_default || prev.validade_dias
    }));
  };

  const applyCatalogTemplate = (modelo) => {
    if (!modelo) return;
    setSelectedTemplateId(null);
    setProposta(prev => ({
      ...prev,
      template_id: undefined,
      titulo: prev.titulo === DEFAULT_PROPOSTA.titulo || !prev.titulo ? modelo.nome : prev.titulo,
      layout: modelo.layout || prev.layout,
      blocos: modelo.blocos && modelo.blocos.length > 0 ? modelo.blocos : prev.blocos,
      modo_design: modelo.modo_design || 'blocos',
      canvas: modelo.canvas?.paginas?.length ? modelo.canvas : prev.canvas,
      itens: modelo.itens_padrao && modelo.itens_padrao.length > 0 ? modelo.itens_padrao : prev.itens,
      validade_dias: modelo.validade_dias_default || prev.validade_dias
    }));
  };

  const handleSelectTemplate = (template) => {
    if (proposta.blocos?.some(b => b.conteudo_html) && !window.confirm('Trocar de modelo substitui o conteúdo atual do documento. Deseja continuar?')) {
      return;
    }
    applyTemplate(template);
  };

  const loadProposta = async () => {
    try {
      setLoading(true);
      const response = await propostasService.buscar(id);
      if (!response.error && response.data) {
        const p = response.data;
        // Orçamento tem tela própria (formulário + miniatura). Links antigos em
        // /propostas/:id/editar continuam válidos e caem lá.
        if (p.tipo === 'orcamento') {
          navigate(caminhoEdicao(p, searchParams.toString()), { replace: true });
          return;
        }
        setProposta(p);
        if (p.lead_id && typeof p.lead_id === 'object') setSelectedLead(p.lead_id);
        if (p.tarefa_id && typeof p.tarefa_id === 'object') setSelectedTarefa(p.tarefa_id);
      } else {
        toast({ title: 'Erro', description: 'Proposta não encontrada', variant: 'destructive' });
        navigate(backTo);
      }
    } catch (error) {
      console.error('Erro ao carregar proposta:', error);
      toast({ title: 'Erro', description: 'Não foi possível carregar a proposta', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (patch) => {
    setProposta(prev => ({ ...prev, ...patch }));
  };

  // Numa proposta já enviada o botão Salvar fica desabilitado (conteúdo imutável),
  // mas a exigência de assinatura ainda pode mudar enquanto o cliente não respondeu
  // — então ela é persistida sozinha, com debounce por causa do campo de termo.
  const handleChangeAssinatura = (assinatura) => {
    handleChange({ assinatura });
    if (!isEditing || proposta.status === 'rascunho') return;

    if (assinaturaDebounceRef.current) clearTimeout(assinaturaDebounceRef.current);
    assinaturaDebounceRef.current = setTimeout(async () => {
      try {
        const response = await propostasService.editar(id, { assinatura });
        if (response.error) throw new Error(response.message);
        toast({
          title: 'Assinatura atualizada',
          description: assinatura?.exigir
            ? 'O cliente precisará assinar para aceitar esta proposta'
            : 'O cliente pode aceitar sem assinar'
        });
      } catch (err) {
        toast({ title: 'Erro', description: err.message || 'Não foi possível salvar a assinatura', variant: 'destructive' });
      }
    }, 600);
  };

  // Chamado pelo AgenteIaPainel (inline na sidebar, não é modal). O histórico da
  // conversa fica em proposta.mensagens_ia — persistido junto com o resto da proposta
  // ao salvar. Atualiza tudo via updater funcional pra evitar closures desatualizadas
  // (o painel pode disparar chamadas em sequência antes do estado assentar).
  const handleGerarComIa = async (instrucao) => {
    const mensagemUsuario = { role: 'user', texto: instrucao || '(gerar a partir do contexto vinculado)', data: new Date().toISOString() };

    try {
      const response = await propostasService.gerarComIa({
        chat_id: proposta.chat_id || undefined,
        lead_id: proposta.lead_id || undefined,
        instrucao,
        // Histórico já trocado com o agente PARA ESTA proposta — sem isso, cada
        // mensagem é tratada isolada e um acompanhamento como "não foi criado" não
        // tem como se referir ao que foi pedido antes.
        historico: (proposta.mensagens_ia || []).map(m => ({ role: m.role, texto: m.texto })),
        // Estado atual do editor — permite ao agente perguntar só o que ainda falta
        // (em vez de repetir perguntas sobre o que o usuário já preencheu).
        estado: {
          titulo: proposta.titulo || '',
          itens: (proposta.itens || []).map(i => ({ descricao: i.descricao, quantidade: i.quantidade, preco_unitario: i.preco_unitario })),
          modo_design: proposta.modo_design || 'blocos',
          fundo_cor: proposta.modo_design === 'canvas' ? corDeFundoCanvas(proposta.canvas) : undefined,
          tem_logo: Boolean(proposta.layout?.logo_url)
        }
      });
      if (response.error) throw new Error(response.message || 'Não foi possível gerar a proposta com IA');

      const { mensagem, titulo, itens, observacoes, forma_pagamento, visual, documento } = response.data || {};

      setProposta(prev => {
        const next = { ...prev };
        if (titulo) next.titulo = titulo;
        if (itens?.length) next.itens = itens;

        // Documento completo gerado pelo agente: reconstrói o canvas inteiro (cabeçalho
        // + seções + tabela de itens). As observações/forma de pagamento já vêm como
        // seções, então o append avulso abaixo é pulado nesse caso.
        const aplicouDocumento = Boolean(documento?.secoes?.length && prev.modo_design === 'canvas');
        if (aplicouDocumento) {
          next.canvas = documentoIaParaCanvas(documento, {
            titulo: titulo || prev.titulo,
            fundoCor: visual?.fundo_cor || corDeFundoCanvas(prev.canvas),
            temaCor: prev.layout?.tema_cor,
            logoUrl: prev.layout?.logo_url,
            empresaNome: prev.layout?.empresa?.nome,
            assinaturaNome: user?.nomeFantasia || user?.nome,
            temContato: Boolean(prev.contato?.nome)
          });
        } else if (visual?.fundo_cor && prev.modo_design === 'canvas' && prev.canvas?.paginas?.length) {
          // Edição visual pontual (ex.: "muda o fundo para verde") — o backend já
          // validou que é um hex. Recolore a camada de fundo quando o documento usa
          // retângulo full-bleed (modelos pré-prontos), senão pinta a folha.
          next.canvas = aplicarCorDeFundo(prev.canvas, visual.fundo_cor);
        }

        if (!aplicouDocumento && (observacoes || forma_pagamento)) {
          if (prev.modo_design === 'canvas') {
            let canvasAtualizado = prev.canvas;
            if (observacoes) canvasAtualizado = adicionarTextoAoCanvas(canvasAtualizado, { titulo: 'Observações', html: observacoes });
            if (forma_pagamento) canvasAtualizado = adicionarTextoAoCanvas(canvasAtualizado, { titulo: 'Forma de pagamento', html: forma_pagamento });
            next.canvas = canvasAtualizado;
          } else {
            const blocosAtualizados = [...(prev.blocos || [])];
            const upsertBloco = (tipo, tituloBloco, html) => {
              const idx = blocosAtualizados.findIndex(b => b.tipo === tipo);
              if (idx >= 0) blocosAtualizados[idx] = { ...blocosAtualizados[idx], conteudo_html: html };
              else blocosAtualizados.push({ tipo, titulo: tituloBloco, conteudo_html: html, config: {}, ordem: blocosAtualizados.length });
            };
            if (observacoes) upsertBloco('observacoes', 'Observações', observacoes);
            if (forma_pagamento) upsertBloco('forma_pagamento', 'Forma de pagamento', forma_pagamento);
            next.blocos = blocosAtualizados;
          }
        }

        // A mensagem do chat é a resposta real do agente (confirmação ou perguntas
        // sobre o que falta). O resumo sintetizado localmente fica só como fallback
        // para respostas antigas/da API sem o campo "mensagem".
        let resumo = mensagem?.trim();
        if (!resumo) {
          const partes = [];
          if (itens?.length) partes.push(`${itens.length} ${itens.length === 1 ? 'item' : 'itens'}`);
          if (titulo) partes.push('o título');
          if (observacoes) partes.push('as observações');
          if (forma_pagamento) partes.push('a forma de pagamento');

          if (itens?.length) {
            resumo = `Pronto! Atualizei ${partes.join(', ')}. Revise os valores antes de enviar ao cliente.`;
          } else if (partes.length > 0) {
            resumo = `Atualizei ${partes.join(', ')}, mas não encontrei preços ou quantidades claros para montar os itens do orçamento. Me diga o que inclui e o valor de cada item (ex.: "manutenção preventiva, R$ 250") que eu adiciono.`;
          } else {
            resumo = 'Não encontrei informações suficientes para montar a proposta — descreva o que inclui, a quantidade e o valor de cada item.';
          }
        }

        next.mensagens_ia = [...(prev.mensagens_ia || []), mensagemUsuario, { role: 'assistant', texto: resumo, data: new Date().toISOString() }];
        return next;
      });
    } catch (error) {
      const textoErro = propostasService.mensagemErroGerarIa(error);
      setProposta(prev => ({
        ...prev,
        mensagens_ia: [...(prev.mensagens_ia || []), mensagemUsuario, { role: 'assistant', texto: textoErro, erro: true, data: new Date().toISOString() }]
      }));
      throw error;
    }
  };

  const handleSelectLead = (lead) => {
    setSelectedLead(lead);
    setProposta(prev => ({
      ...prev,
      lead_id: lead?._id || null,
      chat_id: lead?.whatsapp_chat_id || prev.chat_id,
      contato: lead ? { ...prev.contato, nome: lead.nome } : prev.contato
    }));
  };

  const handleSelectTarefa = (tarefa) => {
    setSelectedTarefa(tarefa);
    setProposta(prev => ({ ...prev, tarefa_id: tarefa?._id || null }));
  };

  const handleSave = async () => {
    if (!proposta.titulo?.trim()) {
      toast({ title: 'Atenção', description: 'Informe um título para a proposta', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        titulo: proposta.titulo,
        lead_id: proposta.lead_id || undefined,
        chat_id: proposta.chat_id || undefined,
        tarefa_id: proposta.tarefa_id || undefined,
        contato: proposta.contato,
        layout: proposta.layout,
        blocos: proposta.blocos,
        modo_design: proposta.modo_design || 'blocos',
        canvas: proposta.canvas,
        itens: proposta.itens,
        desconto_global_percent: proposta.desconto_global_percent,
        validade_dias: proposta.validade_dias,
        mensagens_ia: proposta.mensagens_ia,
        assinatura: proposta.assinatura,
        template_id: !isEditing ? selectedTemplateId : undefined
      };

      const response = isEditing
        ? await propostasService.editar(id, payload)
        : await propostasService.criar(user?.clienteId, payload);

      if (response.error) {
        toast({ title: 'Erro', description: response.message || 'Não foi possível salvar a proposta', variant: 'destructive' });
        return;
      }

      toast({ title: 'Sucesso', description: isEditing ? 'Proposta atualizada' : 'Proposta criada' });
      setProposta(response.data);
      if (!isEditing) {
        setShareDialogOpen(true);
        // Mantém a querystring (voltar/lead_id) na troca de URL: sem isso, salvar
        // uma proposta nova apagava o endereço de retorno e o Voltar deixava de
        // levar de volta ao negócio de origem.
        const qs = searchParams.toString();
        navigate(`/dashboard/propostas/${response.data._id}/editar${qs ? `?${qs}` : ''}`, { replace: true });
      }
    } catch (error) {
      console.error('Erro ao salvar proposta:', error);
      toast({ title: 'Erro', description: 'Não foi possível salvar a proposta', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-sm text-gray-400">Carregando proposta...</span>
      </div>
    );
  }

  const readOnly = isEditing && proposta.status !== 'rascunho';
  // A assinatura só trava depois que o cliente responde (ou já assinou).
  const assinaturaEditavel =
    !['aceita', 'recusada', 'cancelada'].includes(proposta.status) && !proposta.assinatura_cliente?.assinado_em;
  const modoCanvas = proposta.modo_design === 'canvas';

  const handleToggleDesignMode = () => {
    if (modoCanvas) {
      if (window.confirm('Voltar para o formato em blocos? O design livre fica salvo, mas o documento passa a usar os blocos novamente.')) {
        handleChange({ modo_design: 'blocos' });
      }
      return;
    }
    if (window.confirm('Mudar para o design livre? Os blocos atuais viram elementos arrastáveis na página.')) {
      handleChange({
        modo_design: 'canvas',
        canvas: proposta.canvas?.paginas?.length ? proposta.canvas : blocosParaCanvas(proposta)
      });
    }
  };

  const selectedTemplate = templates.find(t => t._id === selectedTemplateId);
  const statusLabel = isEditing
    ? (PROPOSTAS_DESIGN.status[proposta.status]?.label || 'Rascunho')
    : 'Rascunho · ainda não salva';
  const subtituloTopbar = [statusLabel, selectedTemplate?.nome, modoCanvas ? 'Design livre' : 'Blocos'].filter(Boolean).join(' · ');

  return (
    <div className={D.page}>
      <EditorTopBar
        onBack={() => navigate(backTo)}
        isEditing={isEditing}
        titulo={proposta.titulo}
        onChangeTitulo={readOnly ? undefined : (v) => handleChange({ titulo: v })}
        subtitulo={subtituloTopbar}
        saving={saving}
        onSave={handleSave}
        disabled={readOnly}
        canShare={Boolean(proposta._id || id)}
        onShare={() => setShareDialogOpen(true)}
        previewMode={previewMode}
        onTogglePreview={readOnly ? undefined : () => setPreviewMode(v => !v)}
      />

      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Coluna esquerda: painel da proposta (modelo, formato, cliente, resumo,
            assinatura) — ou a conversa com o agente, alargada, no modo conversa */}
        <aside className={`${chatAberto ? 'w-[440px]' : 'w-[320px]'} ${D.sidebar}`}>
          <PropostaSidebar
            selectedLead={selectedLead}
            onSelectLead={handleSelectLead}
            selectedTarefa={selectedTarefa}
            onSelectTarefa={handleSelectTarefa}
            chatVinculado={Boolean(proposta.chat_id)}
            readOnly={readOnly}
            status={proposta.status}
            onGerarComIa={handleGerarComIa}
            mensagensIa={proposta.mensagens_ia}
            chatAberto={chatAberto}
            onToggleChat={setChatAberto}
            propostaId={proposta._id || id}
            assinatura={proposta.assinatura}
            assinaturaEditavel={assinaturaEditavel}
            onChangeAssinatura={handleChangeAssinatura}
            contato={proposta.contato}
            onChangeContato={(patch) => { if (!readOnly) handleChange({ contato: { ...(proposta.contato || {}), ...patch } }); }}
            templates={!isEditing ? templates : []}
            selectedTemplate={selectedTemplate}
            onSelectTemplate={handleSelectTemplate}
            onCreateNewTemplate={() => navigate('/dashboard/propostas/modelos/novo')}
            designMode={proposta.modo_design || 'blocos'}
            onToggleDesignMode={readOnly ? undefined : handleToggleDesignMode}
            itens={proposta.itens}
            descontoGlobalPercent={proposta.desconto_global_percent}
            validadeDias={proposta.validade_dias}
            createdAt={proposta.created_at}
          />
        </aside>

        {/* Centro: documento em edição */}
        {modoCanvas && !readOnly ? (
          <CanvasEditor proposta={proposta} onChange={handleChange} ocultarPaleta={chatAberto} />
        ) : (
          <div className="flex-1 min-w-0 overflow-auto py-8 px-4">
            {modoCanvas ? (
              <CanvasReadOnly proposta={proposta} />
            ) : (
              <DocumentCanvas
                proposta={proposta}
                onChange={handleChange}
                readOnly={readOnly}
                isModelo={false}
              />
            )}
          </div>
        )}

      </div>

      {/* Pré-visualização em modal, como um leitor de PDF (o painel lateral
          espremia o documento numa coluna estreita). */}
      {previewMode && (
        <VisualizadorDocumento
          proposta={proposta}
          modoCanvas={modoCanvas}
          titulo={proposta.titulo || 'Proposta'}
          onClose={() => setPreviewMode(false)}
        />
      )}

      <CompartilharPropostaDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        proposta={proposta}
        temChat={Boolean(proposta?.chat_id)}
      />
    </div>
  );
}
